import { useMemo } from 'react'
import { useTheme, alpha } from '@mui/material/styles'
import EChart from './EChart'
import { fmtHrs } from './vocabulary'

export const LAB_COLORS = {
  stopped: '#4e79a7',
  lost: '#e15759',
}

const tooltipStyle = {
  backgroundColor: 'rgba(33, 33, 41, 0.94)',
  borderWidth: 0,
  textStyle: { color: '#fff', fontSize: 12 },
}

const share = (value) => Math.round(value * 1000) / 10

// One lever across its settings: the share of transcription it would stop and
// the share of quotes it would lose, on one axis. Where the two lines fan
// apart the lever is doing work; where they run together it deletes quotes as
// fast as it saves. The dashed line leaves out the quotes the media rule
// flags, which the media gate would hide anyway.
const LabChart = ({ lever, base, chosen, height = 240 }) => {
  const theme = useTheme()
  const option = useMemo(() => {
    const text = theme.palette.text.secondary
    const grid = alpha(text, 0.15)
    const rows = lever.rows
    const quotes = base.quotes || 0
    const notMedia = rows.map((row) =>
      quotes ? share((row.lost - row.lost_media) / quotes) : 0
    )
    return {
      grid: { left: 44, right: 16, top: 16, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        ...tooltipStyle,
        formatter: (points) => {
          const row = rows[points[0].dataIndex]
          return `<div style="min-width:220px">
            <div style="margin-bottom:4px">${lever.label} at ${
            row.setting
          }</div>
            <div>Stops ${share(row.stopped_share)}% of transcription
              (${fmtHrs(row.stopped_hours_day)} a day)</div>
            <div>Loses ${row.lost} quotes, ${share(row.lost_share)}%</div>
            <div style="opacity:.75">${
              row.lost_media
            } of them flagged as media</div>
          </div>`
        },
      },
      xAxis: {
        type: 'category',
        data: rows.map(({ setting }) => setting),
        axisLabel: { color: text, fontSize: 10, hideOverlap: true },
        axisLine: { lineStyle: { color: grid } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: (extent) => Math.max(10, Math.ceil(extent.max / 10) * 10),
        axisLabel: { color: text, fontSize: 10, formatter: (v) => `${v}%` },
        splitLine: { lineStyle: { color: grid } },
      },
      series: [
        {
          name: 'Transcription stopped',
          type: 'line',
          symbolSize: 6,
          lineStyle: { width: 2.5, color: LAB_COLORS.stopped },
          itemStyle: { color: LAB_COLORS.stopped },
          data: rows.map((row) => share(row.stopped_share)),
          markLine:
            chosen != null && rows[chosen]
              ? {
                  symbol: 'none',
                  silent: true,
                  lineStyle: { color: text, type: 'dashed' },
                  label: { show: false },
                  data: [{ xAxis: rows[chosen].setting }],
                }
              : undefined,
        },
        {
          name: 'Quotes lost',
          type: 'line',
          symbolSize: 6,
          lineStyle: { width: 2.5, color: LAB_COLORS.lost },
          itemStyle: { color: LAB_COLORS.lost },
          data: rows.map((row) => share(row.lost_share)),
        },
        {
          name: 'Quotes lost, not media',
          type: 'line',
          symbol: 'none',
          lineStyle: { width: 1.5, type: 'dashed', color: LAB_COLORS.lost },
          data: notMedia,
        },
      ],
    }
  }, [lever, base, chosen, theme])

  return (
    <EChart
      option={option}
      height={height}
      isEmpty={!lever.rows.length}
      emptyText="This lever has no settings to show."
    />
  )
}

export default LabChart
